/** The read has not returned data and has not failed. */
export interface ReadLoading {
  kind: 'loading';
}

/** The first load failed, so the screen has no data to show. */
export interface ReadFailed {
  kind: 'failed';
  error: unknown;
}

/**
 * The screen has data. `refreshFailed` is true when a later refetch failed and the data
 * may be out of date. The screen shows the data with a StaleNotice. `refreshError` is the error
 * of that refetch. It is present only when `refreshFailed` is true. A hook reads it to tell a
 * refusal such as FORBIDDEN from a network failure.
 */
export interface ReadReady<T> {
  kind: 'ready';
  data: T;
  refreshFailed: boolean;
  refreshError?: unknown;
}

/** What a screen knows about a read. Switch on `kind` and end with a `never` default. */
export type Read<T> = ReadLoading | ReadFailed | ReadReady<T>;

/** The three fields of a query result that `readOf` reads. */
export interface QueryResultFields<T> {
  data: T | undefined;
  isError: boolean;
  error: unknown;
}

/**
 * Turns a query result into a `Read`. The data decides: loaded data is `ready`, also after a
 * failed refetch. TanStack Query sets `isError` and clears `isSuccess` on a failed refetch, and
 * a screen that switches on those flags then hides data that is still on hand. A disabled query
 * with no data reads `loading`, as a pending query does.
 */
export function readOf<T>(query: QueryResultFields<T>): Read<T> {
  if (query.data !== undefined) {
    const refreshFailed = query.isError;
    return refreshFailed
      ? { kind: 'ready', data: query.data, refreshFailed, refreshError: query.error }
      : { kind: 'ready', data: query.data, refreshFailed };
  }
  if (query.isError) return { kind: 'failed', error: query.error };
  return { kind: 'loading' };
}

/**
 * The error behind a read that did not work: the load error of a `failed` read, or the error of
 * the failed refetch of a `ready` read. It is null for a read that has no error.
 */
export function errorOfRead<T>(read: Read<T>): unknown {
  switch (read.kind) {
    case 'failed':
      return read.error;
    case 'ready':
      return read.refreshFailed ? (read.refreshError ?? null) : null;
    case 'loading':
      return null;
    default: {
      const _exhaustive: never = read;
      throw new Error(`Unhandled read kind: ${JSON.stringify(_exhaustive)}`);
    }
  }
}

/**
 * The text of a failed read's error. A tRPC error and a plain `Error` both carry `message`. The
 * check looks at the field, not at the class, so any error-shaped value works.
 */
export function errorMessageOf(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    if (typeof error.message === 'string') return error.message;
  }
  return String(error);
}

/** One `Read` for each element type of `Ts`, in the same order. */
export type ReadsOf<Ts extends unknown[]> = { [K in keyof Ts]: Read<Ts[K]> };

/**
 * `data` holds one entry for each `Read`, in order, and the type parameter lists them in
 * the same order. So the array is that tuple.
 */
function asTuple<Ts extends unknown[]>(data: unknown[]): Ts {
  return data as Ts;
}

/**
 * Joins reads that one screen needs together. It is `failed` when any first load failed, else
 * `loading` when any read loads, else `ready` with the data as a tuple. It is `ready` with
 * `refreshFailed` when any refresh failed. A failed read wins over a loading one, because the
 * screen cannot finish loading.
 */
export function readAll<Ts extends unknown[]>(...reads: ReadsOf<Ts>): Read<Ts> {
  // The loop does not need the element types, so it treats each read as a read of unknown data.
  const list: Read<unknown>[] = reads;
  const data: unknown[] = [];
  let loading = false;
  let refreshFailed = false;
  let refreshError: unknown;
  for (const read of list) {
    switch (read.kind) {
      case 'failed':
        return read;
      case 'loading':
        loading = true;
        break;
      case 'ready':
        data.push(read.data);
        // The first failed refresh gives the error.
        if (read.refreshFailed && !refreshFailed) refreshError = read.refreshError ?? null;
        refreshFailed ||= read.refreshFailed;
        break;
      default: {
        const _exhaustive: never = read;
        throw new Error(`Unhandled read kind: ${JSON.stringify(_exhaustive)}`);
      }
    }
  }
  if (loading) return { kind: 'loading' };
  return refreshFailed
    ? { kind: 'ready', data: asTuple<Ts>(data), refreshFailed: true, refreshError }
    : { kind: 'ready', data: asTuple<Ts>(data), refreshFailed: false };
}
